import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle,
  Clock,
  Calendar,
  User,
  Star,
  X,
  Play,
  FileText,
  File,
  Lock,
  Edit3,
  Save,
  MessageSquare,
} from "lucide-react";
import Header from "../Components/Header";
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  query,
  orderBy,
  getDocs,
} from "firebase/firestore";
import { auth, db } from "../firebase/config";
import RoadmapStudentViewSkeleton from "../Skeleton/RoadmapStudentViewSkeleton";
import { getLevelStyling, getStatusBadgeColor, getStatusColor, getStatusIcon } from "../utils/roadmapStylingHelper";

const RoadmapStudentView = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const [currentUserId, setCurrentUserId] = useState(null);
  const [student, setStudent] = useState(null);
  const [roadmapData, setRoadmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isEditingEvaluation, setIsEditingEvaluation] = useState(false);
  const [evaluationData, setEvaluationData] = useState({
    rating: 0,
    comment: "",
  });
  // New state for hover effect on stars
  const [hoverRating, setHoverRating] = useState(0);

  const initialUnlockProcessed = useRef(false);
  const globalRoadmapStructure = useRef(null);


  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(user => {
      if (user) {
        setCurrentUserId(user.uid);
      } else {
        setCurrentUserId(null);
        setLoading(false);
        setRoadmapData(null);
        setStudent(null);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  const statusMap = {
    "not-started": "pas démarré",
    "in-progress": "en cours",
    completed: "complété",
    locked: "verrouillé",
  }
  // Main data fetching effect for roadmap data
  useEffect(() => {
    if (!currentUserId) {
      return;
    }

    setLoading(true);

    const unsubscribe = [];

    const fetchDataAndSetListeners = async () => {
      try {
        const studentDocRef = doc(db, "users", currentUserId);
        unsubscribe.push(onSnapshot(studentDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const studentData = { id: docSnap.id, ...docSnap.data() };
            setStudent(studentData);
          } else {
            console.error("Student not found for ID:", currentUserId);
            setStudent(null);
          }
        }, (error) => {
          console.error("Error fetching student snapshot:", error);
        }));

        // Fetch global roadmap structure if not already loaded
        if (!globalRoadmapStructure.current) {
          const levelsCollectionRef = collection(db, "roadmapLevels");
          const levelsQuery = query(levelsCollectionRef, orderBy("order"));
          const levelsSnapshot = await getDocs(levelsQuery);

          const fetchedGlobalLevels = [];
          const fetchedGlobalRoadmapItemsFlat = [];

          for (const levelDoc of levelsSnapshot.docs) {
            const levelData = { id: levelDoc.id, ...levelDoc.data() };
            const dynamicStyles = getLevelStyling(levelData.order);

            const sessionsCollectionRef = collection(db, `roadmapLevels/${levelDoc.id}/roadmap_sessions`);
            const sessionsQuery = query(sessionsCollectionRef, orderBy("order"));
            const sessionsSnapshot = await getDocs(sessionsQuery);

            const levelSessions = [];
            sessionsSnapshot.forEach(sessionDoc => {
              const sessionData = { id: sessionDoc.id, ...sessionDoc.data(), levelId: levelDoc.id };
              levelSessions.push(sessionData);
              fetchedGlobalRoadmapItemsFlat.push(sessionData);
            });
            fetchedGlobalLevels.push({ ...levelData, ...dynamicStyles, items: levelSessions });
          }
          fetchedGlobalLevels.sort((a, b) => (a.order || 0) - (b.order || 0));
          globalRoadmapStructure.current = { levels: fetchedGlobalLevels, flatItems: fetchedGlobalRoadmapItemsFlat };
        }

        // Always fetch roadmap data now
        const studentProgressCollectionRef = collection(db, `users/${currentUserId}/roadmapProgress`);
        unsubscribe.push(onSnapshot(studentProgressCollectionRef, async (progressSnapshot) => {
          const studentProgressMap = {};
          progressSnapshot.forEach((progressDoc) => {
            studentProgressMap[progressDoc.id] = progressDoc.data();
          });

          if (!globalRoadmapStructure.current) {
            console.warn("Global roadmap structure not yet loaded. Skipping progress merge.");
            return;
          }

          const mergedRoadmap = {
            student: student,
            levels: globalRoadmapStructure.current.levels.map((level) => {
              let levelCompletedItems = 0;
              let levelInProgressItems = 0;
              let levelTotalItems = level.items.length;

              const itemsWithProgress = level.items.map((globalItem) => {
                const studentItem = studentProgressMap[globalItem.id];
                let status = "locked";
                let resources = [];

                if (studentItem) {
                  status = studentItem.status;
                  if (studentItem.status === "completed") levelCompletedItems++;
                  if (studentItem.status === "in-progress") levelInProgressItems++;
                  resources = studentItem.resources || [];
                } else {
                  resources = [];
                }

                return {
                  ...globalItem,
                  status: status,
                  coachFeedback: studentItem?.coachFeedback || "",
                  selfEvaluation: studentItem?.selfEvaluation || "",
                  rating: studentItem?.rating || 0,
                  unlockedByAdmin: studentItem?.unlockedByAdmin || false,
                  resources: resources,
                };
              });

              const levelProgress =
                levelTotalItems > 0
                  ? Math.round((levelCompletedItems / levelTotalItems) * 100)
                  : 0;

              return {
                ...level,
                items: itemsWithProgress,
                progress: levelProgress,
                completed: levelCompletedItems,
                total: levelTotalItems,
              };
            }),
          };

          if (!initialUnlockProcessed.current) {
            let hasUnlockedItems = Object.values(studentProgressMap).some(
              (p) => p.status !== "locked"
            );

            if (!hasUnlockedItems && globalRoadmapStructure.current.flatItems.length > 0) {
              const firstSessionRef = globalRoadmapStructure.current.flatItems[0];
              console.log("No unlocked items found, attempting to unlock first session:", firstSessionRef.id);
              const sessionProgressRef = doc(db, `users/${currentUserId}/roadmapProgress/${firstSessionRef.id}`);
              await setDoc(sessionProgressRef, {
                sessionId: firstSessionRef.id,
                status: "not-started",
                completionDate: null,
                coachFeedback: "",
                selfEvaluation: "",
                rating: 0,
                unlockedByAdmin: false,
                resources: [],
              }, { merge: true });
              initialUnlockProcessed.current = true;
            } else if (hasUnlockedItems) {
              initialUnlockProcessed.current = true;
            }
          }

          let totalCompletedOverall = 0;
          let totalItemsOverall = 0;
          mergedRoadmap.levels.forEach(level => {
            level.items.forEach(item => {
              totalItemsOverall++;
              if (item.status === "completed") {
                totalCompletedOverall++;
              }
            });
          });
          mergedRoadmap.stats = {
            totalItems: totalItemsOverall,
            inProgress: Object.values(studentProgressMap).filter(p => p.status === "in-progress").length,
            globalProgress: totalItemsOverall > 0 ? Math.round((totalCompletedOverall / totalItemsOverall) * 100) : 0,
          };

          setRoadmapData(mergedRoadmap);
          setLoading(false);
        }, (error) => {
          console.error("Error fetching student progress snapshot:", error);
          setLoading(false);
        }));

      } catch (error) {
        console.error("Error setting up roadmap listeners:", error);
        setLoading(false);
      }
    };

    fetchDataAndSetListeners();

    return () => {
      unsubscribe.forEach(unsub => unsub());
    };
  }, [currentUserId, getStatusColor, getStatusBadgeColor, getStatusIcon]);

  const getCurrentSelectedItem = useCallback(() => {
    if (!selectedItemId || !roadmapData) return null;
    for (const level of roadmapData.levels) {
      const foundItem = level.items.find(item => item.id === selectedItemId);
      if (foundItem) return foundItem;
    }
    return null;
  }, [selectedItemId, roadmapData]);

  const handleSaveEvaluation = async () => {
    const itemToUpdate = getCurrentSelectedItem();
    if (!itemToUpdate || !currentUserId) return;

    try {
      const itemProgressRef = doc(db, `users/${currentUserId}/roadmapProgress/${itemToUpdate.id}`);
      await setDoc(itemProgressRef, {
        selfEvaluation: evaluationData.comment,
        rating: evaluationData.rating,
      }, { merge: true });

      console.log("Self-evaluation saved successfully!");
      setIsEditingEvaluation(false);
      setHoverRating(0); // Reset hover rating after saving
    } catch (error) {
      console.error("Error saving self-evaluation:", error);
    }
  };


  const handleEditEvaluation = (item) => {
    setEvaluationData({
      rating: item.rating || 0,
      comment: item.selfEvaluation || "",
    });
    setHoverRating(item.rating || 0); // Initialize hover rating with current rating
    setIsEditingEvaluation(true);
  };

  const canAddEvaluation = useCallback((item) => {
    return item.status === "completed";
  }, []);

  // renderStars helper function for self-evaluation
  const renderStars = useCallback((rating, editable = false, onStarClick = () => { }, currentHoverRating = 0, onStarHover = () => { }) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const isFilled = editable ? (i <= currentHoverRating || (currentHoverRating === 0 && i <= rating)) : (i <= rating);
      stars.push(
        <Star
          key={i}
          // Increased size to w-5 h-5
          className={`w-5 h-5 ${isFilled ? "text-yellow-400" : "text-gray-500"} ${editable ? "cursor-pointer" : ""}`}
          fill={isFilled ? "currentColor" : "none"}
          onClick={() => editable && onStarClick(i)}
          onMouseEnter={() => editable && onStarHover(i)} // Handle hover for editable stars
          onMouseLeave={() => editable && onStarHover(0)} // Reset hover on leave
        />
      );
    }
    return stars;
  }, []);


  const selectedItem = getCurrentSelectedItem();

  // If loading or student data is not yet available, show skeleton
  if (loading || !student || !roadmapData) {
    return <RoadmapStudentViewSkeleton />;
  }

  return (
    <div className="text-white">
      <Header PageName={"Mon parcours de coaching"} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
   
      <div className="p-4 space-y-6 bg-slate-900">
        <div className="relative rounded-lg bg-gradient-to-br p-6 from-purple-900 via-blue-900 to-indigo-900">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center">
              {student?.profilePhoto ? (
                <img
                  className="rounded-full w-12 h-12 object-cover"
                  src={student.profilePhoto}
                  alt={student.displayName}
                />
              ) : (
                <span className="text-lg font-bold">{student?.displayName ? student.displayName.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}</span>
              )}
            </div>
            <div className="flex items-start flex-col">
              <h1 className="text-2xl font-bold">
                {student?.displayName || "Student"}'s Voyage
              </h1>
              <div className="mb-4">
                <p className="w-fit px-3 py-1 bg-blue-200 text-blue-800 rounded-full text-sm font-medium">
                  {roadmapData.stats.globalProgress}% Complété
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Progression du parcours d’apprentissage</h2>
              <div className="text-right">
                <div className="text-3xl font-bold">{roadmapData.stats.globalProgress}%</div>
                <p className="text-sm text-slate-300">
                  {roadmapData.levels.filter((l) => l.progress === 100).length} of{" "}
                  {roadmapData.levels.length} niveaux terminés
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {roadmapData.levels.map((level) => (
                <div
                  key={level.id}
                  className="bg-slate-800/50 rounded-xl p-4 border border-white/10"
                >
                  <div className="flex space-x-3 mb-3">
                    <div
                      className={`w-8 h-8 ${level.iconBg} rounded-full flex items-center justify-center text-white font-bold text-sm`}
                    >
                      {level.order}
                    </div>
                    <div>
                      <h3 className="text-start font-semibold text-sm">{level.name}</h3>
                      <p className="text-start text-xs text-slate-300">{level.subtitle}</p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-slate-300 mb-1">
                      <span>{level.completed} complété</span>
                      <span>{level.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-2">
                      <div
                        className={`bg-gradient-to-r ${level.color} h-2 rounded-full transition-all duration-300`}
                        style={{ width: `${level.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <p className="text-start text-xs text-slate-400 leading-relaxed">
                    {level.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {roadmapData.levels.map((level) => (
            <div key={level.id}>
              <div className="flex justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-10 h-10 ${level.iconBg} rounded-full flex items-center justify-center text-white font-bold`}
                  >
                    {level.order}
                  </div>
                  <div>
                    <h2 className="text-start text-xl font-bold">{level.name}</h2>
                    <p className="text-start text-slate-400">{level.subtitle}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold">
                    {level.completed}/{level.total}
                  </div>
                  <p className="text-start text-sm text-slate-400">éléments terminés</p>
                </div>
              </div>

              {level.items.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {level.items.map((item, index) => (
                    <div
                      key={item.id}
                      className={`p-4 rounded-xl border transition-all duration-200 hover:shadow-lg cursor-pointer ${getStatusColor(item.status)}`}
                      onClick={() => item.status !== "locked" && setSelectedItemId(item.id)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 ${getStatusBadgeColor(item.status)} rounded font-medium`}>
                            <div className="flex items-center gap-1">
                              {getStatusIcon(item.status)}
                              {statusMap[item.status].replace('-', ' ')}
                            </div>
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {String(item.order).padStart(2, "0")}
                        </span>
                      </div>

                      <h3 className="text-start font-semibold text-white mb-2 text-sm leading-tight">
                        {item.title}
                      </h3>

                      {item.status === "locked" ? (
                        <div className="flex flex-col items-center justify-center h-20 text-slate-400">
                          <Lock className="w-8 h-8 mb-2" />
                          <p className="text-sm">Terminer l'étape précédente</p>
                        </div>
                      ) : (
                        <>
                          {item.description && (
                            <p dangerouslySetInnerHTML={{__html:item.description}} className="text-start text-slate-400 text-xs mb-3 line-clamp-3" />
                             
                          )}

                          <div className="flex items-center space-x-8 text-slate-400 text-sm mb-2">
                            {item.dueDate && (
                              <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4" />
                                <span>Exigible: {item.dueDate}</span>
                              </div>
                            )}
                            {item.estimatedHours && (
                              <div className="flex items-center space-x-2">
                                <Clock className="w-4 h-4" />
                                <span>{item.estimatedHours}h estimées.</span>
                              </div>
                            )}
                          </div>

                          <div className="mt-2 flex items-center justify-start">
                            <p className="flex items-start justify-start gap-1 text-sm text-start text-slate-400">
                              <File className="w-4 h-4" />{" "}
                              {item?.resources?.length || 0} Ressources
                            </p>
                          </div>
                        </>
                      )}
                      {canAddEvaluation(item) && (
                        <div className="mt-3 pt-3 border-t border-slate-600">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-slate-400">Ma note:</span>
                              <div className="flex space-x-1">
                                {renderStars(item.rating)}
                              </div>
                            </div>
                            {item.rating === 0 || !item.selfEvaluation ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedItemId(item.id);
                                  handleEditEvaluation(item);
                                }}
                                className="text-xs text-blue-400 hover:text-blue-300 flex items-center space-x-1"
                              >
                                <MessageSquare className="w-3 h-3" />
                                <span>Ajouter un avis</span>
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedItemId(item.id);
                                  handleEditEvaluation(item);
                                }}
                                className="text-xs text-yellow-400 hover:text-yellow-300 flex items-center space-x-1"
                              >
                                <Edit3 className="w-3 h-3" />
                                <span>Modifier l'avis</span>
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <p>Aucun élément disponible pour ce niveau pour le moment</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {selectedItem && selectedItem.status !== "locked" && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl max-w-2xl w-full max-h-[90vh] scrollbar-thin overflow-y-auto text-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl text-start font-bold">
                  {selectedItem.title}
                </h2>
                <button
                  onClick={() => {
                    setSelectedItemId(null);
                    setIsEditingEvaluation(false);
                  }}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="mb-4 flex items-start">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(selectedItem.status)}`}>
                  {statusMap[selectedItem.status].replace('-', ' ')}
                </span>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-start font-semibold mb-2">
                    Description
                  </h3>
                  <p dangerouslySetInnerHTML={{__html: selectedItem.description}} className="text-start text-slate-300" />
                 
                </div>

                <div className="flex items-center space-x-8 text-slate-400">
                  {selectedItem.dueDate && (
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>Exigible: {selectedItem.dueDate}</span>
                    </div>
                  )}
                  {selectedItem.estimatedHours && (
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4" />
                      <span>{selectedItem.estimatedHours} heures estimées</span>
                    </div>
                  )}
                </div>

                {/* User-Specific Resources Section (Download Only) */}
                <div>
                  <h3 className="text-start font-semibold mb-3">
                    Mes ressources ({selectedItem.resources?.length || 0})
                  </h3>
                  <div className="space-y-3">
                    {selectedItem.resources?.length > 0 ? (
                      selectedItem.resources.map((resource, index) => (
                        <div key={resource.url || index} className="flex items-center p-3 bg-slate-700 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-fit">
                              {resource.type === "video" ? (
                                <Play className="w-5 h-5 text-purple-400" />
                              ) : (
                                <FileText className="w-5 h-5 text-purple-400" />
                              )}
                            </div>
                            <a
                              href={resource.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-300 flex-1 hover:underline truncate"
                              download
                            >
                              {resource.name}
                            </a>
                            <span className="text-xs px-2 py-1 bg-purple-900 text-purple-300 rounded">
                              {resource.type}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-400 text-sm">Aucune ressource attachée pour l'instant.</p>
                    )}
                  </div>
                </div>

                {/* Coach Feedback */}
                {selectedItem.coachFeedback && (
                  <div className="bg-blue-900/20 rounded-lg p-4 border border-blue-700">
                    <div className="text-start flex items-center space-x-2 mb-3">
                      <User className="w-4 h-4 text-blue-400" />
                      <h3 className="text-start font-semibold text-blue-300">
                        Commentaires des entraîneurs
                      </h3>
                    </div>
                    <p className="text-start text-blue-200">
                      {selectedItem.coachFeedback}
                    </p>
                  </div>
                )}

                {/* Self Evaluation Section */}
                {canAddEvaluation(selectedItem) && (
                  <div className="bg-yellow-900/20 rounded-lg p-4 border border-yellow-700">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-start font-semibold text-yellow-300">
                        Mon évaluation
                      </h3>
                      {!isEditingEvaluation && (
                        <button
                          onClick={() => handleEditEvaluation(selectedItem)}
                          className="flex items-center space-x-1 text-yellow-400 hover:text-yellow-300 text-sm"
                        >
                          <Edit3 className="w-4 h-4" />
                          <span>{selectedItem.rating === 0 ? 'Ajouter' : 'Modifier'}</span>
                        </button>
                      )}
                    </div>

                    {!isEditingEvaluation ? (
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="flex space-x-1">
                            {renderStars(selectedItem.rating)}
                          </div>
                          <span className="text-sm text-yellow-500">
                            ({selectedItem.rating}/5)
                          </span>
                        </div>
                        {selectedItem.selfEvaluation ? (
                          <p className="text-start text-yellow-200">
                            {selectedItem.selfEvaluation}
                          </p>
                        ) : (
                          <p className="text-yellow-400 italic">Aucune évaluation ajoutée pour l'instant</p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="text-start block text-sm font-medium text-yellow-300 mb-2">
                            Notation
                          </label>
                          <div className="flex space-x-1"
                            onMouseLeave={() => setHoverRating(0)}> {/* Reset hover on leaving the star container */}
                            {renderStars(
                              evaluationData.rating,
                              true,
                              (rating) => setEvaluationData((prev) => ({ ...prev, rating })),
                              hoverRating, // Pass hoverRating
                              setHoverRating // Pass setHoverRating
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="text-start block text-sm font-medium text-yellow-300 mb-2">
                            Vos réflexions et commentaires
                          </label>
                          <textarea
                            value={evaluationData.comment}
                            onChange={(e) =>
                              setEvaluationData((prev) => ({ ...prev, comment: e.target.value }))
                            }
                            placeholder="Share your experience, what you learned, challenges faced, etc."
                            className="w-full text-slate-200 p-3 bg-slate-700 border border-yellow-500 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
                            rows={4}
                          />
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={handleSaveEvaluation}
                            className="flex items-center space-x-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                          >
                            <Save className="w-4 h-4" />
                            <span>Sauvegarder</span>
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingEvaluation(false);
                              setHoverRating(0); // Reset hover rating on cancel
                            }}
                            className="px-4 py-2 border border-yellow-300 text-yellow-400 rounded-lg hover:bg-yellow-100/10 transition-colors"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoadmapStudentView;
